import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Upload, Link2, X, Download, Eye, Trash2,
  File, FileText, Film, Music, Archive, Image,
  Loader2, AlertCircle, FolderOpen, Camera,
} from 'lucide-react';
import axios from 'axios';
import { apiErrorMessage } from '../../lib/api';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND}/api`;

// ── Helpers ──────────────────────────────────────────────────────────────────

function getFileIcon(mime) {
  if (!mime) return File;
  if (mime.startsWith('image/')) return Image;
  if (mime === 'application/pdf') return FileText;
  if (mime.startsWith('video/')) return Film;
  if (mime.startsWith('audio/')) return Music;
  if (mime.includes('zip') || mime.includes('tar') || mime.includes('rar') || mime.includes('7z')) return Archive;
  if (mime.includes('word') || mime.includes('document') || mime.includes('text')) return FileText;
  return File;
}

function formatSize(bytes) {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ── Preview Modal ─────────────────────────────────────────────────────────────

function PreviewModal({ file, onClose }) {
  const viewUrl = `${BACKEND}${file.view_url}`;
  const downloadUrl = `${viewUrl}?dl=1`;
  const isImage = file.mime?.startsWith('image/');
  const isPdf = file.mime === 'application/pdf';
  const isVideo = file.mime?.startsWith('video/');
  const isAudio = file.mime?.startsWith('audio/');

  // Keyboard close
  useEffect(() => {
    const fn = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [onClose]);

  const handleDownload = async () => {
    try {
      const res = await fetch(downloadUrl);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      window.open(downloadUrl, '_blank');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.80)' }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col"
        style={{ maxWidth: '90vw', width: 900, maxHeight: '92vh' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-3 flex-shrink-0"
          style={{ borderBottom: '1px solid #e5e4df' }}
        >
          <span className="font-semibold text-[#1a1a2e] truncate mr-4" style={{ maxWidth: 480 }}>
            {file.filename}
          </span>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-xs text-[#9ca3af]">{formatSize(file.size)}</span>
            <button
              onClick={handleDownload}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-[#1a1a2e] text-white hover:bg-[#252545] transition-colors"
            >
              <Download className="w-3.5 h-3.5" />
              Download
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div
          className="flex-1 overflow-auto flex items-center justify-center p-4 min-h-0"
          style={{ background: '#f8f7f4' }}
        >
          {isImage && (
            <img
              src={viewUrl}
              alt={file.filename}
              className="max-w-full max-h-full object-contain rounded shadow-lg"
              style={{ maxHeight: 'calc(92vh - 80px)' }}
            />
          )}
          {isPdf && (
            <iframe
              src={viewUrl}
              title={file.filename}
              className="w-full rounded shadow"
              style={{ height: 'calc(92vh - 80px)', border: 'none' }}
            />
          )}
          {isVideo && (
            <video
              src={viewUrl}
              controls
              className="max-w-full rounded shadow-lg"
              style={{ maxHeight: 'calc(92vh - 80px)' }}
            />
          )}
          {isAudio && (
            <div className="text-center py-12">
              <Music className="w-20 h-20 mx-auto text-[#c9a84c]/40 mb-4" />
              <p className="text-[#1a1a2e] font-semibold mb-4">{file.filename}</p>
              <audio src={viewUrl} controls className="mx-auto" />
            </div>
          )}
          {!isImage && !isPdf && !isVideo && !isAudio && (
            <div className="text-center py-12">
              {(() => { const Icon = getFileIcon(file.mime); return <Icon className="w-20 h-20 mx-auto text-[#c9a84c]/40 mb-4" />; })()}
              <p className="text-[#1a1a2e] font-semibold mb-1">{file.filename}</p>
              <p className="text-[#9ca3af] text-sm mb-6">{formatSize(file.size)} · {file.mime}</p>
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 mx-auto px-5 py-2.5 rounded-lg bg-[#c9a84c] text-[#1a1a2e] font-semibold hover:bg-[#b8973e] transition-colors"
              >
                <Download className="w-4 h-4" />
                Download File
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function FilesTab({ project }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(''); // filename being uploaded
  const [urlInput, setUrlInput] = useState('');
  const [urlUploading, setUrlUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef();

  // ── Fetch file list ───────────────────────────────────────────────────────
  const fetchFiles = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/files?project_id=${project.id}`);
      setFiles(res.data.data || []);
    } catch (err) {
      console.error('Failed to fetch files:', err);
      setError(apiErrorMessage(err, 'Could not load the file list.'));
    }
  }, [project.id]);

  useEffect(() => {
    fetchFiles().finally(() => setLoading(false));
  }, [fetchFiles]);

  // ── Core upload helper ────────────────────────────────────────────────────
  const doUploadFiles = useCallback(async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    setError('');
    const arr = Array.from(fileList);
    try {
      for (const f of arr) {
        setUploadProgress(f.name);
        const fd = new FormData();
        fd.append('file', f);
        fd.append('project_id', project.id);
        await axios.post(`${API}/files/upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      await fetchFiles();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Upload failed';
      setError(detail);
    }
    setUploadProgress('');
    setUploading(false);
  }, [project.id, fetchFiles]);

  // ── Clipboard paste (Ctrl+V screenshots) ─────────────────────────────────
  const handlePaste = useCallback((e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const imageItems = Array.from(items).filter(i => i.type.startsWith('image/'));
    if (imageItems.length === 0) return;
    e.preventDefault();
    const pasteFiles = imageItems.map(item => {
      const blob = item.getAsFile();
      if (!blob) return null;
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      return new File([blob], `screenshot_${ts}.png`, { type: 'image/png' });
    }).filter(Boolean);
    if (pasteFiles.length > 0) doUploadFiles(pasteFiles);
  }, [doUploadFiles]);

  useEffect(() => {
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [handlePaste]);

  // ── Clipboard button (navigator.clipboard API) ────────────────────────────
  const handleClipboardBtn = async () => {
    setError('');
    try {
      const items = await navigator.clipboard.read();
      const imgItem = items.find(i =>
        i.types.some(t => t.startsWith('image/'))
      );
      if (!imgItem) {
        setError('No image in clipboard. Take a screenshot (Win+Shift+S / Cmd+Shift+4), then click again.');
        return;
      }
      const mimeType = imgItem.types.find(t => t.startsWith('image/'));
      const blob = await imgItem.getType(mimeType);
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const ext = mimeType.split('/')[1] || 'png';
      const file = new File([blob], `screenshot_${ts}.${ext}`, { type: mimeType });
      await doUploadFiles([file]);
    } catch {
      setError('Clipboard permission denied. Press Ctrl+V anywhere on this page to paste a screenshot.');
    }
  };

  // ── URL import ────────────────────────────────────────────────────────────
  const handleUploadFromUrl = async () => {
    const url = urlInput.trim();
    if (!url) return;
    setUrlUploading(true);
    setError('');
    try {
      await axios.post(`${API}/files/upload-from-url`, {
        url,
        project_id: project.id,
      });
      setUrlInput('');
      await fetchFiles();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to import from URL');
    }
    setUrlUploading(false);
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async (file) => {
    if (!window.confirm(`Delete "${file.filename}"?\nThis removes it from storage but keeps any Drive copy.`)) return;
    try {
      await axios.delete(`${API}/files/${encodeURIComponent(file.gcs_blob_name)}`);
      setFiles(prev => prev.filter(f => f.id !== file.id));
    } catch {
      setError('Delete failed');
    }
  };

  // ── Drag & drop ───────────────────────────────────────────────────────────
  const handleDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = () => setDragOver(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    doUploadFiles(e.dataTransfer.files);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* ── Upload zone ── */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-xl p-8 text-center mb-5 transition-all ${
          dragOver
            ? 'border-[#c9a84c] bg-[#c9a84c]/8 scale-[1.01]'
            : 'border-[#d1d5db] hover:border-[#c9a84c]/60'
        }`}
      >
        <Upload className="w-10 h-10 mx-auto mb-3 text-[#c9a84c]/60" />
        <p className="text-[#1a1a2e] font-semibold mb-1">Drop files here to upload</p>
        <p className="text-[#9ca3af] text-sm mb-4">All file types supported · Max 50 MB each</p>

        <div className="flex flex-wrap justify-center gap-2">
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-[#c9a84c] text-[#1a1a2e] hover:bg-[#b8973e] disabled:opacity-50 transition-colors"
          >
            <Upload className="w-3.5 h-3.5" />
            Browse Files
          </button>
          <button
            onClick={handleClipboardBtn}
            disabled={uploading}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-[#1a1a2e] text-white hover:bg-[#252545] disabled:opacity-50 transition-colors"
            title="Take a screenshot, then click here (or just press Ctrl+V)"
          >
            <Camera className="w-3.5 h-3.5" />
            Paste Screenshot
          </button>
        </div>

        <p className="text-[#9ca3af] text-xs mt-3">
          💡 Take a screenshot → press <kbd className="bg-gray-100 px-1 py-0.5 rounded text-xs font-mono">Ctrl+V</kbd> anywhere on this page
        </p>

        {uploading && (
          <div className="flex items-center justify-center gap-2 mt-3 text-[#c9a84c] text-sm">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Uploading{uploadProgress ? ` ${uploadProgress}` : ''}…</span>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={e => { doUploadFiles(e.target.files); e.target.value = ''; }}
        />
      </div>

      {/* ── URL import ── */}
      <div className="flex gap-2 mb-5">
        <div className="relative flex-1">
          <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9ca3af]" />
          <input
            type="url"
            placeholder="Paste a file URL to import (image, PDF, document…)"
            value={urlInput}
            onChange={e => setUrlInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleUploadFromUrl()}
            className="w-full pl-9 pr-4 py-2 text-sm border border-[#e5e4df] rounded-lg focus:outline-none focus:ring-1 focus:ring-[#c9a84c] focus:border-[#c9a84c] bg-white"
          />
        </div>
        <button
          onClick={handleUploadFromUrl}
          disabled={urlUploading || !urlInput.trim()}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-[#1a1a2e] text-white hover:bg-[#252545] disabled:opacity-40 transition-colors"
        >
          {urlUploading
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : <Download className="w-4 h-4" />}
          Import URL
        </button>
      </div>

      {/* ── Error banner ── */}
      {error && (
        <div className="flex items-start gap-2 p-3 mb-4 rounded-lg bg-red-50 text-red-600 text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="flex-shrink-0 hover:text-red-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* ── File list ── */}
      {loading ? (
        <div className="text-center py-14">
          <Loader2 className="w-8 h-8 mx-auto animate-spin text-[#c9a84c]/40" />
        </div>
      ) : files.length === 0 ? (
        <div className="text-center py-14">
          <FolderOpen className="w-16 h-16 mx-auto text-[#c9a84c]/30 mb-3" />
          <p className="text-[#6b7280] font-medium">No files yet</p>
          <p className="text-[#9ca3af] text-sm mt-1">Upload files, paste a screenshot, or import from a URL</p>
        </div>
      ) : (
        <>
          <p className="text-xs font-semibold uppercase tracking-wider text-[#9ca3af] mb-2">
            {files.length} file{files.length !== 1 ? 's' : ''}
          </p>
          <div className="space-y-1">
            {files.map(file => {
              const IconComp = getFileIcon(file.mime);
              const viewUrl = `${BACKEND}${file.view_url}`;
              const isImage = file.mime?.startsWith('image/');
              return (
                <div
                  key={file.id}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#f3f2ef] transition-colors group"
                  style={{ borderBottom: '1px solid rgba(201,168,76,0.08)' }}
                >
                  {/* Icon / thumbnail */}
                  {isImage ? (
                    <img
                      src={viewUrl}
                      alt={file.filename}
                      className="w-9 h-9 rounded object-cover flex-shrink-0 cursor-pointer shadow-sm"
                      onClick={() => setPreview(file)}
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div className="w-9 h-9 rounded flex items-center justify-center flex-shrink-0 bg-[#c9a84c]/10">
                      <IconComp className="w-4 h-4 text-[#c9a84c]" />
                    </div>
                  )}

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p
                      className="text-sm font-medium text-[#1a1a2e] truncate cursor-pointer hover:text-[#c9a84c] transition-colors"
                      onClick={() => setPreview(file)}
                    >
                      {file.filename}
                    </p>
                    <p className="text-xs text-[#9ca3af]">
                      {formatSize(file.size)}
                      {file.created_at && ` · ${formatDate(file.created_at)}`}
                      {file.uploader && ` · ${file.uploader}`}
                      {file.drive_file_id && (
                        <span className="ml-1.5 text-emerald-500 font-semibold">✓ Drive</span>
                      )}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => setPreview(file)}
                      className="p-1.5 rounded hover:bg-[#c9a84c]/10 text-[#9ca3af] hover:text-[#c9a84c] transition-colors"
                      title="Preview"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          const res = await fetch(`${viewUrl}?dl=1`);
                          const blob = await res.blob();
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url; a.download = file.filename; a.click();
                          URL.revokeObjectURL(url);
                        } catch { window.open(`${viewUrl}?dl=1`, '_blank'); }
                      }}
                      className="p-1.5 rounded hover:bg-blue-50 text-[#9ca3af] hover:text-blue-500 transition-colors"
                      title="Download"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(file)}
                      className="p-1.5 rounded hover:bg-red-50 text-[#9ca3af] hover:text-red-500 transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ── Preview modal ── */}
      {preview && <PreviewModal file={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}
